import React, {useCallback, useEffect, useMemo, useRef, useState} from 'react'
import NotificationContext from '@/components/NotificationContext/NotificationContext'
import {
  AlertColor,
  Snackbar,
  SnackbarContent,
  SnackbarContentProps,
  SnackbarOrigin,
} from '@mui/material'
import {styled} from '@mui/system'
import {useAppDispatch, useAppSelector} from '@/hooks/redux'
import {selectJWT} from '@/store/selectors/storage'
import {selectIsFailLoad, selectUser} from '@/store/selectors'
import useCentrifugeNotification from '@/hooks/useCentrifugeNotification'
import {colors} from '@/constants'
import {SET_FAIL_LOAD} from '@/store/slices'
import {loadAllTranslations} from '@/services/i18n/loadTranslations'

interface NotificationProviderProps {
  children: any
}

const colorsNotification = {
  success: {text: colors.brown, bg: colors.white},
  info: {text: colors.brown, bg: colors.white},
  warning: {text: colors.white, bg: '#480C07'},
  error: {text: colors.white, bg: '#480C07'},
}

const CustomSnackbarContent = styled(SnackbarContent)<SnackbarContentProps>(
  () => ({
    color: '#FFFFFF',
    minWidth: '100vw',
    maxWidth: '100vw',
    position: 'fixed',
    justifyContent: 'center',
    bottom: 0,
    right: 0,
    padding: '16px',
    fontSize: '1.25rem',
  })
)

const anchorOrigin: SnackbarOrigin = {
  vertical: 'bottom',
  horizontal: 'right',
}

const NotificationProvider = ({children}: NotificationProviderProps) => {
  const jwtToken = useAppSelector(selectJWT)
  const user = useAppSelector(selectUser)
  const isFailLoad = useAppSelector(selectIsFailLoad)
  const dispatch = useAppDispatch()

  const jwtTokenRef = useRef(jwtToken)
  useEffect(() => {
    jwtTokenRef.current = jwtToken
  }, [jwtToken])

  const [notification, setNotification] = useState<{
    open: boolean
    message: string
    severity: AlertColor
  }>({
    open: false,
    message: '',
    severity: 'success',
  })

  const {subscribe, lastMessage, setData, unsubscribe} =
    useCentrifugeNotification(process.env.REACT_APP_URL_CENTRIFUGE!)

  const showNotification = useCallback(
    (message: string, severity: AlertColor = 'info') => {
      setNotification({open: true, message, severity})
    },
    []
  )

  const handleClose = useCallback(
    (_event?: React.SyntheticEvent | Event, reason?: string) => {
      if (reason === 'clickaway') return
      setNotification((prev) => ({...prev, open: false}))
    },
    []
  )

  useEffect(() => {
    if (user) {
      setData({token: jwtTokenRef.current, id: user.id})
    } else {
      setData(null)
    }
  }, [user])

  useEffect(() => {
    if (isFailLoad) {
      showNotification(
        'Error: function is not available, try again later.',
        'error'
      )
      dispatch(SET_FAIL_LOAD(false))
    }
  }, [isFailLoad])

  useEffect(() => {
    void loadAllTranslations()
  }, [])

  const contextValue = useMemo(
    () => ({showNotification, subscribe, unsubscribe, lastMessage}),
    [showNotification, subscribe, unsubscribe, lastMessage]
  )

  return (
    <NotificationContext.Provider value={contextValue}>
      {children}
      <Snackbar
        open={notification.open}
        autoHideDuration={2000}
        onClose={handleClose}
        anchorOrigin={anchorOrigin}>
        <CustomSnackbarContent
          message={notification.message}
          style={{
            borderRadius: '3px',
            background: colorsNotification[notification.severity].bg,
            color: colorsNotification[notification.severity].text,
          }}
        />
      </Snackbar>
    </NotificationContext.Provider>
  )
}

export default NotificationProvider
